import { localizedStrings } from '@core/strings'
import React, { useEffect } from 'react'
import { useModal } from 'webapp/components/common/GlobalModal'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { Controller, useForm } from 'react-hook-form'
import { RegisterItemInspectionProperty } from '@core/entities/register/RegisterItem'
import { ModalDialogContainer } from '@common/modal/ModalDialogContainer'
import { PageUrls } from '@core/page-urls'
import { useRouter } from 'next/router'
import { encodeReturnUrl } from 'webapp/utils'
import { useDrawer } from '@common/GlobalDrawer'

interface NewInspectionModalProps {
  organisationId: number
  projectId: number
  registerId: number
  registerItemId: number
  inspections: RegisterItemInspectionProperty<string>[]
  registerTemplateInspectionId?: string
}

type FormSchema = {
  registerTemplateInspectionId: string
  inspectionTemplateId: number
}

export const NewInspectionModal: React.FC<NewInspectionModalProps> = ({
  organisationId,
  projectId,
  registerId,
  registerItemId,
  registerTemplateInspectionId,
  inspections,
}) => {
  const { hideModal } = useModal()
  const { hideDrawer } = useDrawer()
  const router = useRouter()

  const { control, watch, handleSubmit, setValue } = useForm<FormSchema>({
    defaultValues: {
      registerTemplateInspectionId,
      inspectionTemplateId: undefined,
    },
  })

  const formRegisterTemplateInspectionId = watch('registerTemplateInspectionId')

  const inspectionTemplates =
    inspections.find(
      ({ inspection }) =>
        inspection?.registerTemplateInspectionId ===
        formRegisterTemplateInspectionId,
    )?.inspection?.checklistTemplates ?? []

  useEffect(() => {
    if (inspectionTemplates.length === 1) {
      setValue('inspectionTemplateId', inspectionTemplates[0].id, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    } else {
      setValue('inspectionTemplateId', undefined)
    }
  }, [inspectionTemplates, setValue])

  const onSubmit = (data: FormSchema) => {
    const returnUrl = encodeReturnUrl({
      pathname: router.pathname,
      query: router.query,
    })

    router.push({
      pathname: PageUrls.newChecklistProject({
        organisationId: organisationId,
        projectId: projectId,
      }),
      query: {
        registerId,
        registerItemId,
        projectId: projectId,
        registerTemplateInspectionId: data.registerTemplateInspectionId,
        inspectionTemplateId: data.inspectionTemplateId,
        returnUrl,
        isUsedForInspections: true,
      },
    })

    hideDrawer()
    hideModal()
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20, mb: 0 }}
      >
        {localizedStrings.inspection}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
        }}
      >
        {!registerTemplateInspectionId && (
          <Controller
            name='registerTemplateInspectionId'
            control={control}
            rules={{ required: localizedStrings.pleaseSelectAnInspection }}
            render={({ field, fieldState }) => (
              <TextField
                placeholder={localizedStrings.selectInspection}
                label={localizedStrings.selectInspection}
                error={!!fieldState.error}
                sx={{ mt: 2 }}
                fullWidth
                select
                {...field}
              >
                {inspections?.map(({ inspection, name }) => (
                  <MenuItem
                    key={inspection?.registerTemplateInspectionId}
                    value={inspection?.registerTemplateInspectionId}
                  >
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        )}

        <Controller
          name='inspectionTemplateId'
          control={control}
          rules={{ required: localizedStrings.pleaseSelectATemplate }}
          render={({
            field: { value, onChange, ...fieldProps },
            fieldState,
          }) => (
            <TextField
              placeholder={localizedStrings.selectTemplate}
              label={localizedStrings.selectTemplate}
              error={!!fieldState.error}
              sx={{ mt: 2 }}
              fullWidth
              select
              value={value ?? ''}
              onChange={onChange}
              {...fieldProps}
            >
              {!inspectionTemplates.length && (
                <MenuItem disabled value=''>
                  {localizedStrings.noOptions}
                </MenuItem>
              )}
              {inspectionTemplates.map((inspectionTemplate) => (
                <MenuItem
                  key={inspectionTemplate.id}
                  value={inspectionTemplate.id}
                >
                  {inspectionTemplate.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <Button
          variant='contained'
          data-test='submit-register-inspection'
          onClick={handleSubmit(onSubmit)}
        >
          {localizedStrings.submit}
        </Button>
      </DialogActions>
    </ModalDialogContainer>
  )
}
